import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  User, Mail, Phone, MapPin, Briefcase, GraduationCap, 
  Building2, Award, BookOpen, UserPlus, MessageSquare,
  Crown, Star, Lock, Globe, Linkedin, Calendar, Users,
  Eye, ExternalLink, Check, Pencil, TrendingUp, Activity,
  Heart, MessageCircle, Share2, ChevronRight, FileText,
  Sparkles, BarChart3, UserCheck, Pin, Plus, X, Folder, Image,
  ScrollText, Unlock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectProfilePage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('sobre');
  const [showAddHighlightModal, setShowAddHighlightModal] = useState(false);
  const [highlightType, setHighlightType] = useState('post');
  const [newProject, setNewProject] = useState({ title: '', description: '', image_url: '', link_url: '' });

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      fetchUserProfile(currentUser.id);
    }
    fetchProfile();
    fetchUserPosts();
  }, [profileId, currentUser, isAuthenticated]);

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/connect/profiles/user/${userId}`);
      if (response.data.exists) {
        setUserProfile(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar perfil do usuário:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      let url = `${API}/connect/profiles/${profileId}`;
      if (userProfile?.profile_id) {
        url += `?viewer_id=${userProfile.profile_id}`;
      }
      
      const response = await axios.get(url);
      setProfile(response.data);
    } catch (error) {
      toast.error('Perfil nao encontrado');
      navigate('/connect');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API}/connect/posts?author_id=${profileId}&limit=5`);
      setUserPosts(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar publicações do perfil:', error);
    }
  };

  const handleConnect = async () => {
    if (!userProfile) {
      toast.error('Crie seu perfil primeiro para conectar');
      navigate('/connect/create-profile');
      return;
    }

    try {
      await axios.post(`${API}/connect/connections?requester_id=${userProfile.profile_id}`, {
        target_profile_id: profileId
      });
      toast.success('Solicitacao de conexao enviada!');
      setConnectionStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar solicitacao');
    }
  };

  const handleMessage = () => {
    if (!userProfile) {
      toast.error('Crie seu perfil primeiro');
      navigate('/connect/create-profile');
      return;
    }
    navigate(`/connect/messages/${profileId}`);
  };

  // Highlight functions
  const handlePinPost = async (postId) => {
    try {
      await axios.post(`${API}/connect/profiles/${profileId}/highlights/pin-post`, {
        post_id: postId
      });
      toast.success('Post adicionado aos destaques!');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao destacar post');
    }
  };

  const handleAddProject = async () => {
    if (!newProject.title.trim()) {
      toast.error('Titulo do projeto e obrigatorio');
      return;
    }
    try {
      await axios.post(`${API}/connect/profiles/${profileId}/highlights/add-project`, newProject);
      toast.success('Projeto adicionado aos destaques!');
      setNewProject({ title: '', description: '', image_url: '', link_url: '' });
      setShowAddHighlightModal(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar projeto');
    }
  };

  const handleRemoveHighlight = async (index) => {
    try {
      await axios.delete(`${API}/connect/profiles/${profileId}/highlights/${index}`);
      toast.success('Destaque removido!');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover destaque');
    }
  };

  const isPostHighlighted = (postId) => {
    return profile?.highlights?.some(h => h.type === 'post' && h.post_id === postId);
  };

  const getPlanBadge = (plan) => {
    switch(plan) {
      case 'basic':
      case 'student':
        return <Badge className="bg-blue-600 text-white" title="Membro do plano Básico — contato liberado"><Unlock className="w-3 h-3 mr-1" />Plano Básico</Badge>;
      case 'plus':
      case 'professional':
        return <Badge className="bg-orange-600 text-white"><Star className="w-3 h-3 mr-1" />Plano Plus</Badge>;
      case 'premium':
      case 'company':
        return <Badge className="bg-purple-600 text-white"><Crown className="w-3 h-3 mr-1" />Plano Premium</Badge>;
      default:
        return null;
    }
  };

  const getProfileTypeName = (type) => {
    const types = {
      student: 'Estudante',
      professional: 'Profissional',
      company: 'Empresa',
      institution: 'Instituicao'
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Hoje';
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays} dias atras`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atras`;
    return date.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const isOwner = currentUser && profile.user_id === currentUser.id;

  // Highlights Section Component
  const HighlightsSection = () => {
    const highlights = profile.highlights || [];
    const canAddMore = highlights.length < 3;
    
    if (!isOwner && highlights.length === 0) return null;
    
    return (
      <Card className="bg-zinc-900 border-zinc-800" data-testid="highlights-section">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Pin className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-semibold text-white">Destaques</h2>
          </div>
          {isOwner && canAddMore && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddHighlightModal(true)}
              className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
              data-testid="add-highlight-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Adicionar
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {highlights.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {highlights.map((highlight, index) => (
                <div 
                  key={highlight.id || `highlight-${highlight.title}-${highlight.type}-${index}`} 
                  className="group relative bg-zinc-800/50 rounded-lg overflow-hidden border border-zinc-700/50 hover:border-yellow-600/30 transition-all"
                >
                  {/* Image/Thumbnail */}
                  <div className="aspect-video bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center">
                    {highlight.image_url ? (
                      <img src={highlight.image_url} alt={highlight.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-4">
                        {highlight.type === 'post' ? (
                          <FileText className="w-8 h-8 text-gray-500 mx-auto" />
                        ) : (
                          <Folder className="w-8 h-8 text-yellow-500 mx-auto" />
                        )}
                      </div>
                    )}
                    {/* Type badge */}
                    <div className="absolute top-2 left-2">
                      <Badge className={highlight.type === 'post' ? 'bg-blue-600/80' : 'bg-yellow-600/80'}>
                        {highlight.type === 'post' ? 'Post' : 'Projeto'}
                      </Badge>
                    </div>
                    {/* Remove button (owner only) */}
                    {isOwner && (
                      <button
                        onClick={() => handleRemoveHighlight(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-600/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-highlight-${index}`}
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-3">
                    <h3 className="text-white font-medium text-sm line-clamp-2">{highlight.title}</h3>
                    {highlight.description && (
                      <p className="text-gray-400 text-xs mt-1 line-clamp-2">{highlight.description}</p>
                    )}
                    {highlight.link_url && (
                      <a 
                        href={highlight.link_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-blue-400 mt-2 hover:text-blue-300"
                      >
                        Ver mais <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : isOwner ? (
            <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
              <Pin className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm mb-2">Nenhum destaque ainda</p>
              <p className="text-gray-500 text-xs mb-4">Destaque seus melhores posts ou projetos para visitantes verem primeiro</p>
              <Button 
                size="sm" 
                onClick={() => setShowAddHighlightModal(true)}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Adicionar destaque
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  // Tab content components
  const TabSobre = () => (
    <div className="space-y-6">
      {/* Highlights - Show first */}
      <HighlightsSection />
      
      {/* About */}
      {profile.bio && (
        <Card className="bg-zinc-900 border-zinc-800" data-testid="about-section">
          <CardHeader className="pb-3">
            <h2 className="text-lg font-semibold text-white">Sobre</h2>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 whitespace-pre-line leading-relaxed">{profile.bio}</p>
          </CardContent>
        </Card>
      )}

      {/* Activity/Posts Preview */}
      {userPosts.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800" data-testid="activity-section">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Atividade</h2>
              <p className="text-sm text-gray-500">{userPosts.length} publicacoes</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setActiveTab('atividade')}
              className="text-blue-400 hover:text-blue-300"
            >
              Ver todas <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {userPosts.slice(0, 2).map((post) => (
              <div key={post.id} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                <p className="text-gray-300 text-sm line-clamp-3">{post.content}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Heart className="w-3 h-3" /> {post.likes_count || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> {post.comments_count || 0}
                  </span>
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const TabAtividade = () => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="pb-3">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          Todas as Publicacoes
        </h2>
      </CardHeader>
      <CardContent className="space-y-4">
        {userPosts.length > 0 ? (
          userPosts.map((post) => (
            <div key={post.id} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
                    {profile.profile_photo ? (
                      <img src={profile.profile_photo} alt={profile.name} className="w-full h-full object-cover" />
                    ) : (
                      profile.name?.charAt(0)?.toUpperCase()
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">{profile.name}</p>
                    <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
                  </div>
                </div>
                {/* Pin button for owner */}
                {isOwner && !isPostHighlighted(post.id) && (profile.highlights?.length || 0) < 3 && (
                  <button
                    onClick={() => handlePinPost(post.id)}
                    className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-zinc-700 rounded-lg transition-colors"
                    title="Destacar este post"
                    data-testid={`pin-post-${post.id}`}
                  >
                    <Pin className="w-4 h-4" />
                  </button>
                )}
                {isOwner && isPostHighlighted(post.id) && (
                  <Badge className="bg-yellow-600/20 text-yellow-400 text-xs">
                    <Pin className="w-3 h-3 mr-1" />
                    Destaque
                  </Badge>
                )}
              </div>
              <p className="text-gray-300 whitespace-pre-line">{post.content}</p>
              <div className="flex items-center gap-6 mt-4 pt-3 border-t border-zinc-700/50">
                <button className="flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors">
                  <Heart className="w-4 h-4" />
                  <span className="text-sm">{post.likes_count || 0}</span>
                </button>
                <button className="flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm">{post.comments_count || 0}</span>
                </button>
                <button className="flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors">
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">Compartilhar</span>
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Nenhuma publicacao ainda</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const TabExperiencia = () => (
    <div className="space-y-6">
      {/* Experience */}
      <Card className="bg-zinc-900 border-zinc-800" data-testid="experience-section">
        <CardHeader className="pb-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-400" />
            Experiencia
          </h2>
        </CardHeader>
        <CardContent>
          {profile.experiences && profile.experiences.length > 0 ? (
            <div className="space-y-6">
              {profile.experiences.map((exp, idx) => (
                <div key={exp.id || `exp-${exp.company}-${exp.title}-${idx}`} className="flex gap-4 group">
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0 group-hover:bg-zinc-700 transition-colors">
                    <Building2 className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{exp.title}</h3>
                    <p className="text-gray-400">{exp.company}</p>
                    <p className="text-sm text-gray-500 flex items-center mt-1">
                      <Calendar className="w-3 h-3 mr-1" />
                      {exp.start_date} - {exp.is_current ? 'Presente' : exp.end_date || 'N/A'}
                      {exp.location && (
                        <>
                          <span className="mx-2">|</span>
                          <MapPin className="w-3 h-3 mr-1" />
                          {exp.location}
                        </>
                      )}
                    </p>
                    {exp.description && (
                      <p className="text-gray-400 text-sm mt-2 leading-relaxed">{exp.description}</p>
                    )}
                    {exp.skills && exp.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {exp.skills.map((skill, i) => (
                          <Badge key={`${skill}-${i}`} className="bg-zinc-800 text-gray-400 text-xs">{skill}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Nenhuma experiencia adicionada</p>
              {isOwner && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3 border-zinc-700 text-gray-400"
                  onClick={() => navigate(`/connect/profile/${profileId}/edit`)}
                >
                  Adicionar experiencia
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const TabFormacao = () => (
    <div className="space-y-6">
      {/* Education */}
      <Card className="bg-zinc-900 border-zinc-800" data-testid="education-section">
        <CardHeader className="pb-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-400" />
            Formacao Academica
          </h2>
        </CardHeader>
        <CardContent>
          {profile.education && profile.education.length > 0 ? (
            <div className="space-y-6">
              {profile.education.map((edu, idx) => (
                <div key={edu.id || `edu-${edu.institution}-${edu.degree}-${idx}`} className="flex gap-4 group">
                  <div className="w-12 h-12 rounded-lg bg-purple-600/20 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-600/30 transition-colors">
                    <BookOpen className="w-6 h-6 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{edu.institution}</h3>
                    <p className="text-gray-400">
                      {edu.degree}
                      {(edu.field_of_study || edu.field) && ` em ${edu.field_of_study || edu.field}`}
                    </p>
                    {(edu.start_date || edu.end_date || edu.year) && (
                      <p className="text-sm text-gray-500 mt-1">
                        {edu.year ? edu.year : `${edu.start_date || ''} - ${edu.end_date || 'Presente'}`}
                      </p>
                    )}
                    {edu.description && (
                      <p className="text-gray-400 text-sm mt-2">{edu.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <GraduationCap className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Nenhuma formacao adicionada</p>
              {isOwner && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3 border-zinc-700 text-gray-400"
                  onClick={() => navigate(`/connect/profile/${profileId}/edit`)}
                >
                  Adicionar formacao
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complementary Courses */}
      {profile.courses && profile.courses.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800" data-testid="courses-section">
          <CardHeader className="pb-3">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-400" />
              Cursos Complementares
            </h2>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {profile.courses.map((course, idx) => (
                <div key={course.id || `course-${course.name}-${course.institution}-${idx}`} className="flex gap-4 p-3 bg-zinc-800/50 rounded-lg group hover:bg-zinc-800 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white">{course.name}</h3>
                    <p className="text-sm text-gray-400">{course.institution}</p>
                    {course.year && (
                      <p className="text-xs text-gray-500 mt-1">{course.year}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const TabCertificacoes = () => (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="certifications-section">
      <CardHeader className="pb-3">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-400" />
          Licencas e Certificacoes
        </h2>
      </CardHeader>
      <CardContent>
        {profile.certifications && profile.certifications.length > 0 ? (
          <div className="space-y-4">
            {profile.certifications.map((cert, idx) => (
              <div key={cert.id || `cert-${cert.name}-${cert.issuer}-${idx}`} className="flex gap-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 hover:border-yellow-600/30 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-yellow-600/20 flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{cert.name}</h3>
                  <p className="text-gray-400">{cert.issuer}</p>
                  {(cert.issue_date || cert.year) && (
                    <p className="text-sm text-gray-500 mt-1">
                      Emitido em {cert.issue_date || cert.year}
                    </p>
                  )}
                  {cert.credential_id && (
                    <p className="text-xs text-gray-500 mt-1">
                      ID da credencial: {cert.credential_id}
                    </p>
                  )}
                  {cert.credential_url && (
                    <a 
                      href={cert.credential_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-blue-400 hover:text-blue-300 mt-2"
                    >
                      Ver credencial <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Award className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Nenhuma certificacao adicionada</p>
            {isOwner && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3 border-zinc-700 text-gray-400"
                onClick={() => navigate(`/connect/profile/${profileId}/edit`)}
              >
                Adicionar certificacao
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const tabs = [
    { id: 'sobre', label: 'Sobre', icon: User },
    { id: 'atividade', label: 'Atividade', icon: Activity },
    { id: 'experiencia', label: 'Experiencia', icon: Briefcase },
    { id: 'formacao', label: 'Formacao', icon: GraduationCap },
    { id: 'certificacoes', label: 'Certificacoes', icon: Award }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'sobre': return <TabSobre />;
      case 'atividade': return <TabAtividade />;
      case 'experiencia': return <TabExperiencia />;
      case 'formacao': return <TabFormacao />;
      case 'certificacoes': return <TabCertificacoes />;
      default: return <TabSobre />;
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="profile-page">
      <Header />
      
      {/* Cover & Profile Header */}
      <section className="pt-20">
        {/* Cover Image */}
        <div className="h-48 md:h-56 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 relative overflow-hidden">
          {profile.cover_photo ? (
            <img src={profile.cover_photo} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLThoLTJ2LTRoMnY0em0tOCA4aC0ydi00aDJ2NHptMC04aC0ydi00aDJ2NHoiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
            </div>
          )}
        </div>

        <div className="container mx-auto px-4">
          <div className="relative -mt-16 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-end gap-6">
              {/* Profile Photo */}
              <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 border-4 border-black flex items-center justify-center text-white text-4xl md:text-5xl font-bold overflow-hidden shadow-xl">
                {profile.profile_photo ? (
                  <img src={profile.profile_photo} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  profile.name?.charAt(0)?.toUpperCase()
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-white truncate" data-testid="profile-name">
                    {profile.name}
                  </h1>
                  {getPlanBadge(profile.plan)}
                  {profile.is_professor && (
                    <Badge className="bg-red-600/20 text-red-400 border border-red-500/30" data-testid="professor-badge">
                      <GraduationCap className="w-3 h-3 mr-1" />
                      Professor AJ | BIM
                    </Badge>
                  )}
                  {profile.is_featured && (
                    <Badge className="bg-yellow-600/20 text-yellow-400">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Destaque
                    </Badge>
                  )}
                </div>
                
                <p className="text-base md:text-lg text-gray-300 mb-2" data-testid="profile-headline">
                  {profile.headline || `${getProfileTypeName(profile.profile_type)} BIM`}
                </p>
                
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-400">
                  {profile.location && (
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {profile.location}
                    </span>
                  )}
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {profile.connections_count || 0} conexoes
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-4 lg:mt-0">
                {isOwner ? (
                  <Button 
                    onClick={() => navigate(`/connect/profile/${profileId}/edit`)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar Perfil
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={handleConnect}
                      disabled={connectionStatus === 'pending'}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="connect-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {connectionStatus === 'pending' ? 'Pendente' : 'Conectar'}
                    </Button>
                    <Button 
                      onClick={handleMessage}
                      variant="outline"
                      className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
                      data-testid="message-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Mensagem
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Analytics Card (Owner only) */}
              {isOwner && (
                <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-zinc-700" data-testid="analytics-section">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-medium flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-400" />
                        Analytics do Perfil
                      </h3>
                      <Badge className="bg-blue-600/20 text-blue-400">Privado</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Eye className="w-4 h-4 text-gray-400" />
                          <span className="text-2xl font-bold text-white">{profile.profile_views || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500">Visualizacoes</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-2xl font-bold text-white">{profile.connections_count || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500">Conexoes</p>
                      </div>
                      <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-gray-400" />
                          <span className="text-2xl font-bold text-white">{userPosts.length}</span>
                        </div>
                        <p className="text-xs text-gray-500">Publicacoes</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tabs Navigation */}
              <div className="flex overflow-x-auto hide-scrollbar border-b border-zinc-800 -mx-4 px-4 lg:mx-0 lg:px-0">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'text-blue-400 border-blue-400'
                          : 'text-gray-400 border-transparent hover:text-gray-300'
                      }`}
                      data-testid={`tab-${tab.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Contact Info */}
              <Card className="bg-zinc-900 border-zinc-800" data-testid="contact-section">
                <CardHeader className="pb-3">
                  <h2 className="text-base font-semibold text-white">Informacoes de Contato</h2>
                </CardHeader>
                <CardContent className="space-y-3">
                  {profile.contact_visible || isOwner ? (
                    <>
                      {profile.email && (
                        <a href={`mailto:${profile.email}`} className="flex items-center text-gray-300 hover:text-blue-400 transition-colors">
                          <Mail className="w-4 h-4 mr-3 text-gray-500" />
                          <span className="text-sm truncate">{profile.email}</span>
                        </a>
                      )}
                      {profile.phone && (
                        <div className="flex items-center text-gray-300">
                          <Phone className="w-4 h-4 mr-3 text-gray-500" />
                          <span className="text-sm">{profile.phone}</span>
                        </div>
                      )}
                      {profile.whatsapp && (
                        <a 
                          href={`https://wa.me/${profile.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-green-400 hover:text-green-300 transition-colors"
                        >
                          <Phone className="w-4 h-4 mr-3" />
                          <span className="text-sm">WhatsApp</span>
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                      {profile.website && (
                        <a 
                          href={profile.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-gray-300 hover:text-blue-400 transition-colors"
                        >
                          <Globe className="w-4 h-4 mr-3 text-gray-500" />
                          <span className="text-sm">Website</span>
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                      {profile.linkedin && (
                        <a 
                          href={profile.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-gray-300 hover:text-blue-400 transition-colors"
                        >
                          <Linkedin className="w-4 h-4 mr-3 text-gray-500" />
                          <span className="text-sm">LinkedIn</span>
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                      {profile.lattes && (
                        <a 
                          href={profile.lattes}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-gray-300 hover:text-blue-400 transition-colors"
                          data-testid="lattes-link"
                        >
                          <ScrollText className="w-4 h-4 mr-3 text-gray-500" />
                          <span className="text-sm">Currículo Lattes</span>
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <Lock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm mb-3">
                        Contato visivel para membros premium
                      </p>
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => navigate('/connect/plans')}
                      >
                        Ver Planos
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Skills */}
              {profile.skills && profile.skills.length > 0 && (
                <Card className="bg-zinc-900 border-zinc-800" data-testid="skills-section">
                  <CardHeader className="pb-3">
                    <h2 className="text-base font-semibold text-white">Competencias</h2>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill, idx) => (
                        <Badge key={`${skill}-${idx}`} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors cursor-default">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Services (for companies) */}
              {profile.services && profile.services.length > 0 && (
                <Card className="bg-zinc-900 border-zinc-800" data-testid="services-section">
                  <CardHeader className="pb-3">
                    <h2 className="text-base font-semibold text-white">Servicos Oferecidos</h2>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {profile.services.map((service, idx) => (
                        <li key={`${service}-${idx}`} className="flex items-center text-gray-300 text-sm">
                          <Check className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Upgrade Prompt (for free users viewing their own profile) */}
              {isOwner && profile.plan === 'free' && (
                <Card className="bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border-blue-700/50" data-testid="upgrade-prompt">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600/30 flex items-center justify-center flex-shrink-0">
                        <UserCheck className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">Destaque seu perfil</h3>
                        <p className="text-sm text-gray-400 mb-3">
                          Obtenha o selo de verificado e apareca em destaque nas buscas.
                        </p>
                        <Button 
                          size="sm" 
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => navigate('/connect/plans')}
                        >
                          Ver Planos
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Profile Info */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center text-gray-400">
                      <span>Tipo de perfil</span>
                      <span className="text-white">{getProfileTypeName(profile.profile_type)}</span>
                    </div>
                    {profile.industry && (
                      <div className="flex justify-between items-center text-gray-400">
                        <span>Setor</span>
                        <span className="text-white">{profile.industry}</span>
                      </div>
                    )}
                    {profile.company_size && (
                      <div className="flex justify-between items-center text-gray-400">
                        <span>Tamanho</span>
                        <span className="text-white">{profile.company_size} funcionarios</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-gray-400">
                      <span>Membro desde</span>
                      <span className="text-white">
                        {profile.created_at ? new Date(profile.created_at).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Add Highlight Modal */}
      {showAddHighlightModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Pin className="w-5 h-5 text-yellow-400" />
                Adicionar Destaque
              </h3>
              <button 
                onClick={() => setShowAddHighlightModal(false)}
                className="p-1 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              {/* Type selector */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setHighlightType('post')}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    highlightType === 'post' 
                      ? 'border-blue-500 bg-blue-600/20 text-blue-400' 
                      : 'border-zinc-700 text-gray-400 hover:border-zinc-600'
                  }`}
                >
                  <FileText className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm">Fixar Post</span>
                </button>
                <button
                  onClick={() => setHighlightType('project')}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    highlightType === 'project' 
                      ? 'border-yellow-500 bg-yellow-600/20 text-yellow-400' 
                      : 'border-zinc-700 text-gray-400 hover:border-zinc-600'
                  }`}
                >
                  <Folder className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm">Adicionar Projeto</span>
                </button>
              </div>

              {highlightType === 'post' ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400">
                    Selecione um post na aba "Atividade" e clique no icone de fixar
                    <Pin className="w-3 h-3 inline ml-1" />
                  </p>
                  <Button 
                    onClick={() => {
                      setShowAddHighlightModal(false);
                      setActiveTab('atividade');
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Ir para Atividade
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Titulo do Projeto *</label>
                    <input
                      type="text"
                      value={newProject.title}
                      onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                      placeholder="Ex: Projeto BIM Residencial"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:outline-none"
                      data-testid="project-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Descricao</label>
                    <textarea
                      value={newProject.description}
                      onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                      placeholder="Breve descricao do projeto..."
                      rows={2}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:outline-none resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">URL da Imagem</label>
                    <input
                      type="url"
                      value={newProject.image_url}
                      onChange={(e) => setNewProject({...newProject, image_url: e.target.value})}
                      placeholder="https://exemplo.com/imagem.jpg"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Link do Projeto</label>
                    <input
                      type="url"
                      value={newProject.link_url}
                      onChange={(e) => setNewProject({...newProject, link_url: e.target.value})}
                      placeholder="https://exemplo.com/meu-projeto"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <Button 
                    onClick={handleAddProject}
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                    data-testid="add-project-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Projeto
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hide scrollbar style */}
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ConnectProfilePage;
